import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { withRouter } from 'react-router-dom';
import { RootAppState } from 'store/types';
import withNavigation from 'components/hocs/withNavigation';
import { getTreeOfContentVisibility } from 'store/treeOfContent/selectors';
import {
  getSections,
  getNonInfoContentQuestions,
  getAffectProgressQuestionsCountInSection,
  getAnsweredAffectProgressQuestionsCountInSection,
  getSectionIndex,
  getSection,
  isLearningObjective
} from 'store/sections/selectors';
import { localize } from 'core/localization';
import * as settingSelectors from 'store/settings/selectors';
import * as questionActions from 'store/questions/actions';
import * as courseTimerActions from 'store/timer/actions';
import { getTimerElapsed, getTimerStatus } from 'store/timer/selectors';
import { TimerStatus } from 'constants/timer';
import { Separator } from 'components/common';
import { getQuestionUrl, getLearningObjectiveUrl } from 'utils/navigation';
import { getCourseTitle } from 'store/course/selectors';
import { getElementIDInViewPort } from 'store/navigation/selectors';
import { getSectionId, getPoolQuestions } from 'store/questions/selectors';
import debounce from 'lodash.debounce';
import { DEBOUNCE_SCROLL_WAIT_TIME_MS, SKIP_TO_CONTENT_IDS } from 'constants/common';
import {
  isLearningObjectiveView,
  isQuestionView
} from 'components/shell/RouteTransition/viewChecker';
import { getCurrentLocationHash } from 'utils/window';
import QuestionPage from '../../question/QuestionPage';
import QuestionPageSubmitOnce from '../../question/QuestionPageSubmitOnce';
import LearningObjective from '../../learningObjective/LearningObjective';

import {
  StickyContainer,
  StickyHead,
  SectionTitle,
  SectionNumber,
  SectionName,
  StickyBody,
  LearningObjectiveBlock,
  QuestionBlock,
  CourseLogo,
  ProgressContainer,
  Progress,
  SectionCounter,
  SectionHeader,
  SectionSeparator,
  BLOCK_MARGIN_OFFSET
} from './OnePageCourse.styled';

type OnePageCourseProps = {
  sectionId: any;
  questionId: any;
  sections: any[];
  isCourseOnOnePage: boolean;
  isOverallMasteryScore: boolean;
  shouldSubmitAllQuestions: boolean;
  getSectionById(id: string): string;
  scrollToId(id: string, offset?: number): void;
  elementIdInViewport: string;
  score: number;
  navigateToUrl(url: string, isReplace: boolean): void;
  logoShown: boolean;
  logoUrl: string;
  affectProgressQuestionsCountInSection(sectionId: string): number;
  answeredAffectProgressQuestionsCountInSection(sectionId: string): number;
  match: { [key: string]: any };
  history: { [key: string]: any };
  location: { [key: string]: any };
  sectionIndex(sectionId: string): number;
  section(sectionId: string): { [key: string]: any };
  isExpanded: boolean;
  actions: { [key: string]: any };
  title: string;
  isLearningObjective(sectionId: string): boolean;
  timerActions: { [key: string]: any };
  isTimerEnabled: boolean;
  timerStatus: TimerStatus;
  timeElapsed: number;
  isQuestionPool: boolean;
  questionsForPool: any[];
};

type LocationMap = {
  [key: string]: string;
};

const calculateLocationMap = (props: any) => {
  const locationMap: { [x: string]: string } = {};
  const populateLocationMap = (id: string) => {
    const elementEndPosition =
      (document.getElementById(`block-end-${id}`)?.offsetTop || 0) - BLOCK_MARGIN_OFFSET;
    locationMap[elementEndPosition.toString()] = id;
    return locationMap;
  };

  if (!props.isQuestionPool) {
    props.sections.forEach((section: any) => {
      if (section.learningObjective) {
        populateLocationMap(section.id);
      }

      section.questions.forEach((questionId: string) => {
        populateLocationMap(questionId);
      });
    });

    return locationMap;
  }

  props.questionsForPool.forEach((question: any) => {
    populateLocationMap(question.id);
  });

  return locationMap;
};

const Header = (props: {
  match: { [key: string]: any };
  sectionIndex(sectionId: string): number;
  section(sectionId: string): { [key: string]: any };
}) => {
  const { match, sectionIndex, section } = props;
  const { sectionId } = match.params;
  return (
    <SectionTitle>
      <SectionNumber>Section {sectionIndex(sectionId) + 1}</SectionNumber>
      <SectionName>{section(sectionId).title}</SectionName>
    </SectionTitle>
  );
};

const ProgressBar = (props: {
  match: { [key: string]: any };
  affectProgressQuestionsCountInSection(sectionId: string): number;
  answeredAffectProgressQuestionsCountInSection(sectionId: string): number;
}) => {
  const {
    match,
    answeredAffectProgressQuestionsCountInSection,
    affectProgressQuestionsCountInSection
  } = props;
  const { sectionId } = match.params;
  const answeredQuestionsCount = answeredAffectProgressQuestionsCountInSection(sectionId);
  const allQuestionsCount = affectProgressQuestionsCountInSection(sectionId);
  const progress = (answeredQuestionsCount / allQuestionsCount) * 100;
  return (
    <ProgressContainer>
      <Progress progress={progress} />
    </ProgressContainer>
  );
};

const SectionBreaker = (props: { sections: any[]; section: any; index: number }) => (
  <SectionSeparator>
    <SectionCounter>
      {localize('[section x of y]', { index: props.index + 1, total: props.sections.length })}
    </SectionCounter>
    <SectionHeader>{props.section.title}</SectionHeader>
    <Separator margin={48} opacity={0.1} color="textColor" />
  </SectionSeparator>
);

const renderLearningObjective = (section: any, isQuestionPool: boolean) =>
  section.learningObjective &&
  !isQuestionPool && (
    <LearningObjectiveBlock key={section.id} id={section.id}>
      <LearningObjective className="LearningObjective" sectionId={section.id} />
      <div id={`block-end-${section.id}`} />
    </LearningObjectiveBlock>
  );

const getFirstElementId = (sections: any) => {
  const [firstSection] = sections;
  if (firstSection.learningObjective) {
    return firstSection.id;
  }
  return firstSection.questions[0];
};

const getLearningObjectiveTitlePosition = (id: string) => {
  const learningObjectiveDiv = document.getElementById(id);
  if (!learningObjectiveDiv) {
    return 0;
  }
  const learningObjectiveTitle = learningObjectiveDiv.querySelectorAll('div')[0];
  return learningObjectiveTitle
    ? learningObjectiveTitle.offsetTop + learningObjectiveTitle.scrollHeight / 2
    : 0;
};

const getQuestionIndex = (
  questionId: string,
  sectionIndex: number,
  props: { sections: any[]; questionsForPool: any[]; isQuestionPool: boolean }
) => {
  if (props.isQuestionPool) {
    return props.questionsForPool.map(question => question.id).indexOf(questionId);
  }
  return props.sections[sectionIndex].nonInfoContentQuestions.indexOf(questionId);
};

const getQuestionCount = (
  sectionIndex: number,
  props: { sections: any[]; questionsForPool: any[]; isQuestionPool: boolean }
) => {
  if (props.isQuestionPool) {
    return props.questionsForPool.length;
  }
  return props.sections[sectionIndex].nonInfoContentQuestions.length;
};

const questionProps = (
  questionId: string,
  sectionId: string,
  index: number,
  shouldRecalculateScrollLocation: any,
  props: any
) => {
  const onePageIndex: any[] = [];
  const { isQuestionPool, questionsForPool, sections } = props;
  let theLastID: string;
  if (isQuestionPool && questionsForPool.length) {
    theLastID = questionsForPool[questionsForPool.length - 1].id;
  } else {
    sections.forEach((section: any) => {
      if (section.learningObjective) {
        onePageIndex.push(section.id);
      }
      section.questions.forEach((innerElement: any) => {
        onePageIndex.push(innerElement);
      });
    });
    theLastID = onePageIndex[onePageIndex.length - 1];
    onePageIndex.shift();
  }
  return {
    sectionId,
    questionId,
    currentScrollLocationId: questionId,
    isLastBlock: questionId === theLastID,
    onePageQuestionIndex: getQuestionIndex(questionId, index, props),
    questionCountInOnePage: getQuestionCount(index, props),
    shouldRecalculateScrollLocation,
    match: props.match,
    location: props.location,
    history: props.history
  };
};

const renderQuestionBlock = (
  props: any,
  setShouldRecalculateScrollLocation: any,
  questionId: string,
  questionIndex: number,
  section: any,
  sectionIndex?: number
) => {
  const isFirstElement = props.isQuestionPool
    ? questionIndex === 0 && !props.logoShown
    : questionIndex === 0 &&
      sectionIndex === 0 &&
      !section.learningObjective &&
      !props.logoShown &&
      !props.isOverallMasteryScore;

  return (
    <QuestionBlock key={questionId} isFirstElement={isFirstElement} id={questionId}>
      <div className="question-block">
        {props.shouldSubmitAllQuestions ? (
          <QuestionPageSubmitOnce
            {...questionProps(
              questionId,
              section.id,
              sectionIndex || 0,
              setShouldRecalculateScrollLocation,
              props
            )}
          />
        ) : (
          <QuestionPage
            {...questionProps(
              questionId,
              section.id,
              sectionIndex || 0,
              setShouldRecalculateScrollLocation,
              props
            )}
          />
        )}

        <div id={`block-end-${questionId}`} />
      </div>
    </QuestionBlock>
  );
};

export const OnePageCourse = (props: OnePageCourseProps) => {
  const [locationMap, setLocationMap] = useState<LocationMap>({});
  const [shouldRecalculateScrollLocation, setShouldRecalculateScrollLocation] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [learningObjectiveQuestion, setLearningObjectiveQuestion] = useState(false);
  const elementInViewportRef = useRef(
    props.elementIdInViewport || getFirstElementId(props.sections)
  );

  const getActiveSectionID = (elementId: string) => {
    if (props.isLearningObjective(elementId)) {
      return elementId;
    }
    return props.getSectionById(elementId);
  };
  useEffect(() => {
    document.title = `${localize('[course]')} | ${props.title}`;
  });

  useEffect(() => {
    if (props.elementIdInViewport !== '') {
      const isFirstSection =
        props.sectionIndex(getActiveSectionID(props.elementIdInViewport)) === 0;
      const getOffsetValue = () => (isFirstSection && props.logoShown ? 300 : 100);
      props.scrollToId(props.elementIdInViewport, getOffsetValue());
    }
    const newLocationMap = calculateLocationMap(props);
    setLocationMap(newLocationMap);
    if (props.elementIdInViewport && props.elementIdInViewport !== elementInViewportRef.current) {
      elementInViewportRef.current = props.elementIdInViewport;
    }
  }, [props.elementIdInViewport]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newLocationMap = calculateLocationMap(props);
    setLocationMap(newLocationMap);
    setShouldRecalculateScrollLocation(false);
  }, [shouldRecalculateScrollLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToUrl = useCallback(
    debounce((url: string) => {
      const currentPathname = getCurrentLocationHash().replace('#', '');
      if (isQuestionView.test(currentPathname) || isLearningObjectiveView.test(currentPathname)) {
        props.navigateToUrl(url, true);
      }
    }, DEBOUNCE_SCROLL_WAIT_TIME_MS),
    []
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const getIdInViewport = (currentPosition: number) => {
    const blockEndPositions = Object.keys(locationMap).map(position => parseFloat(position));
    const scrollableContainer = document.getElementById('stickyContainer');
    const lastPosition = blockEndPositions[blockEndPositions.length - 1];
    if (scrollableContainer) {
      const hasReachedBottom =
        Math.floor(scrollableContainer.scrollHeight - scrollableContainer.scrollTop) ===
        scrollableContainer.clientHeight;
      if (hasReachedBottom && lastPosition) {
        return locationMap[lastPosition];
      }
    }

    const startPosition = blockEndPositions.reduce((acc, val) => {
      if (val >= currentPosition && acc < currentPosition) {
        return val;
      }
      return acc;
    }, blockEndPositions[0]);

    const pageTop = document.getElementById('stickyContainer')?.scrollTop || 0;
    const selectedElement = document.getElementById(locationMap[startPosition]);
    const elementTop = selectedElement?.offsetTop;
    const isElementTopVisible = pageTop <= (elementTop || 0);

    return isElementTopVisible ? locationMap[startPosition] : elementInViewportRef.current;
  };

  const stickyContainerOnScroll = () => {
    const { isTimerEnabled, timerActions, timeElapsed, timerStatus } = props;
    const stickyContainer: any = document.getElementById('stickyContainer');
    const currentScrollPosition = stickyContainer.scrollTop;
    const currentComponentId = getIdInViewport(currentScrollPosition);

    setScrollPosition(currentScrollPosition);
    setLearningObjectiveQuestion(
      props.isLearningObjective(currentComponentId) &&
        currentScrollPosition <= getLearningObjectiveTitlePosition(currentComponentId)
    );

    if (currentComponentId !== elementInViewportRef.current) {
      elementInViewportRef.current = currentComponentId;

      if (props.isLearningObjective(elementInViewportRef.current)) {
        navigateToUrl(getLearningObjectiveUrl(elementInViewportRef.current));
      } else {
        const sectionId = (
          props.sections.find(
            (section: any) => section.questions.indexOf(elementInViewportRef.current) >= 0
          ) || props.sections[0]
        ).id;

        if (elementInViewportRef.current) {
          props.actions.markAsOpened(elementInViewportRef.current);
          navigateToUrl(getQuestionUrl(sectionId, elementInViewportRef.current));
          if (
            isTimerEnabled &&
            (timerStatus === TimerStatus.NOT_STARTED || timerStatus === TimerStatus.RESET)
          ) {
            timerActions.startTimer();
            timerActions.setTimerElapsed(timeElapsed);
          }
        }
      }
    }
  };

  const { sections, isExpanded } = props;

  const renderStickyBody = (section: any, index: number) => (
    <div key={section.id}>
      <StickyBody>
        {props.logoShown && index === 0 && (
          <CourseLogo logoUrl={props.logoUrl} altText={localize('[alt logo login]')} />
        )}
        {renderLearningObjective(section, props.isQuestionPool)}
        {props.isOverallMasteryScore && !section.learningObjective && (
          <SectionBreaker sections={props.sections} section={section} index={index} />
        )}
        {section.questions.map((innerElement: any, innerIndex: number) =>
          renderQuestionBlock(
            props,
            setShouldRecalculateScrollLocation,
            innerElement,
            innerIndex,
            section,
            index
          )
        )}
      </StickyBody>
    </div>
  );

  const renderQuestionPoolStickyBody = () => (
    <div>
      <StickyBody>
        {props.logoShown && (
          <CourseLogo logoUrl={props.logoUrl} altText={localize('[alt logo login]')} />
        )}
        {props.questionsForPool.map((poolQuestion: any, index: number) =>
          renderQuestionBlock(props, setShouldRecalculateScrollLocation, poolQuestion.id, index, {
            id: poolQuestion.sectionId
          })
        )}
      </StickyBody>
    </div>
  );

  return (
    <StickyContainer
      id={SKIP_TO_CONTENT_IDS.ONE_PAGE_COURSE}
      background={true}
      onScroll={() => stickyContainerOnScroll()}
    >
      {!props.isOverallMasteryScore && scrollPosition !== 0 && !learningObjectiveQuestion && (
        <StickyHead isExpanded={isExpanded}>
          <Header match={props.match} sectionIndex={props.sectionIndex} section={props.section} />
          <ProgressBar
            match={props.match}
            affectProgressQuestionsCountInSection={props.affectProgressQuestionsCountInSection}
            answeredAffectProgressQuestionsCountInSection={
              props.answeredAffectProgressQuestionsCountInSection
            }
          />
        </StickyHead>
      )}
      {!props.isQuestionPool &&
        sections.map((section: any, index: number) => renderStickyBody(section, index))}
      {props.isQuestionPool && renderQuestionPoolStickyBody()}
    </StickyContainer>
  );
};

function mapStateToProps(state: RootAppState) {
  return {
    logoUrl: settingSelectors.getLogo(state),
    logoShown: settingSelectors.isMainLogoShown(state),
    sections: getSections(state).map(section => ({
      ...section,
      nonInfoContentQuestions: getNonInfoContentQuestions(state, section.id)
    })),
    isCourseOnOnePage: settingSelectors.isAllQuestionsOnOnePage(state),
    isOverallMasteryScore: settingSelectors.isOverallMasteryScore(state),
    shouldSubmitAllQuestions: settingSelectors.shouldSubmitAllQuestions(state),
    elementIdInViewport: getElementIDInViewPort(state),
    sectionId: (questionId: string) => getSectionId(state, questionId),
    getSectionById: (questionId: string) => getSectionId(state, questionId),
    affectProgressQuestionsCountInSection: (sectionId: string) =>
      getAffectProgressQuestionsCountInSection(state, sectionId),
    answeredAffectProgressQuestionsCountInSection: (sectionId: string) =>
      getAnsweredAffectProgressQuestionsCountInSection(state, sectionId),
    sectionIndex: (sectionId: string) => getSectionIndex(state, sectionId),
    section: (sectionId: string) => getSection(state, sectionId),
    isExpanded: getTreeOfContentVisibility(state),
    title: getCourseTitle(state),
    isLearningObjective: (sectionId: string) => isLearningObjective(state, sectionId),
    timerStatus: getTimerStatus(state),
    isTimerEnabled: settingSelectors.getTimerEnabled(state),
    timeElapsed: getTimerElapsed(state),
    questionsForPool: getPoolQuestions(state),
    isQuestionPool: settingSelectors.isQuestionPoolEnabled(state)
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(questionActions, dispatch),
    timerActions: bindActionCreators(courseTimerActions, dispatch)
  };
}

export default withRouter(
  withNavigation(connect(mapStateToProps, mapDispatchToProps)(OnePageCourse))
);
